// Quote Matrix PDF export — extracted from QuoteDetailPage so it can be unit-
// reasoned about and tested in isolation. The logic is highly sensitive
// (sandbox-aware download, text-overlay alignment, section-aware pagination,
// attachment merging) — see PRD changelog 2026-06-12 through 2026-06-17 for
// the full RCA history before touching anything here.
//
// Call from a React component as:
//   await runQuoteMatrixExport({ q, attachments, includeAttachments, id, api, toast, setBusy: setExportBusy, fmtAuditDate });
//
// ── INVARIANTS — DO NOT CHANGE WITHOUT RE-RUNNING ─────────────────────────────
// 1. `PLANNER_CAPTURE_WIDTH = 820` — sets the CSS pixel width the planner is
//    pinned to during snapshotting. Drives the html2canvas raster width AND
//    the CSS-px → PDF-pt scale. Smaller width → larger visible text. Tuned
//    via user feedback (1200/960px were both too small).
// 2. JPEG quality `0.92` for the planner snapshot — anything lower causes
//    visible compression artifacts on borders/dividers.
// 3. Baseline coefficient `0.55` and the `r.bottom - lineHeight/2 + fontSize*0.55`
//    formula — derived empirically by pixel-diffing the selectable-text overlay
//    against the rasterised glyphs. Values 0.12/0.28/0.40 each left ~2-3px
//    upward drift (text-selection highlights below the visible word).
// 4. Per-word `charSpace = (renderedWidthPt - pdfWidthPt) / (text.length - 1)`
//    — makes the invisible PDF Helvetica run match the visible Geist glyph-run
//    width so the highlight box covers 100% of each word (not ~70%).
// 5. `safeOverlay` semantics: text-overlay failures MUST be logged + continued,
//    NEVER abort the export. The previous regression (4× recurrence) was caused
//    by overlay exceptions bubbling out of the outer try/catch and skipping the
//    attachment-merge step. Keep every overlay walk inside a per-page try/catch.
// 6. The display-restore (`node.style.display = prevDisplay`) MUST happen AFTER
//    both the onclone() walker and the page-rendering loop have finished. If
//    restored mid-flight the cloned DOM's getBoundingClientRect() returns 0s.
// 7. Sandbox bypass: the merged PDF MUST be POSTed to
//    `/api/quotes/{id}/temporary-export` and downloaded via the returned token
//    URL with `target="_top"`. Direct blob:-URL downloads are silently blocked
//    by the Emergent preview iframe sandbox (no `allow-downloads` flag).
//    Backend contract is locked by `/app/backend/tests/test_pdf_export_pipeline.py`.
// 8. Attachment payload shape consumed below: `{ kind, url, original_name,
//    uploaded_at, uploaded_by?, parsed_email? }`. `kind` ∈ {pdf, supplier_pdf,
//    image, msg, eml}. Any new attachment kind needs a new branch in the merge
//    loop AND a test in `test_pdf_export_pipeline.py::test_attachments_*`.
// ──────────────────────────────────────────────────────────────────────────────

export async function runQuoteMatrixExport({ q, attachments, includeAttachments, id, api, toast, setBusy, fmtAuditDate }) {
  setBusy(true);
  const log = (msg) => console.log(`[QM-PDF] ${msg}`);
  const t0 = performance.now();
  log("Step 1/8 — start");
  try {
    log("Step 2/8 — dynamic import html2canvas/jspdf/pdf-lib");
    const [{ default: html2canvas }, { jsPDF }, { PDFDocument, StandardFonts, rgb }] = await Promise.all([
      import("html2canvas"),
      import("jspdf"),
      import("pdf-lib"),
    ]);
    const node = document.getElementById("planner-sheet");
    if (!node) throw new Error("Planner sheet not found");
    const prevDisplay = node.style.display;
    const prevWidth = node.style.width;
    node.style.display = "block";
    const PLANNER_CAPTURE_WIDTH = 820;
    node.style.width = `${PLANNER_CAPTURE_WIDTH}px`;
    void node.offsetWidth;
    const nodeRect = node.getBoundingClientRect();
    log(`Step 3/8 — html2canvas snapshotting (planner=${nodeRect.width.toFixed(0)}x${nodeRect.height.toFixed(0)})`);

    const wordPositions = [];
    const breakCandidates = [0];
    // scale 2 was Retina-grade but doubled the canvas pixel work. 1.75 keeps
    // a sharp planner snapshot on Retina screens while shaving ~22 % off the
    // most expensive step of the export pipeline.
    const canvas = await html2canvas(node, {
      scale: 1.75,
      useCORS: true,
      backgroundColor: "#ffffff",
      windowWidth: PLANNER_CAPTURE_WIDTH,
      onclone: (clonedDoc, clonedNode) => {
        try {
          const clonedRect = clonedNode.getBoundingClientRect();
          const cwin = clonedDoc.defaultView || window;
          clonedNode.querySelectorAll('[data-pdf-section]').forEach((el) => {
            const r = el.getBoundingClientRect();
            breakCandidates.push(r.top - clonedRect.top);
            breakCandidates.push(r.bottom - clonedRect.top);
          });
          clonedNode.querySelectorAll('[data-pdf-section] tbody > tr').forEach((tr) => {
            const r = tr.getBoundingClientRect();
            breakCandidates.push(r.bottom - clonedRect.top);
          });
          const walker = clonedDoc.createTreeWalker(clonedNode, NodeFilter.SHOW_TEXT, {
            acceptNode: (n) => {
              const t = (n.nodeValue || "").trim();
              if (!t) return NodeFilter.FILTER_REJECT;
              let el = n.parentElement;
              while (el && el !== clonedNode) {
                const cs = cwin.getComputedStyle(el);
                if (cs.display === "none" || cs.visibility === "hidden" || Number(cs.opacity) === 0) {
                  return NodeFilter.FILTER_REJECT;
                }
                el = el.parentElement;
              }
              return NodeFilter.FILTER_ACCEPT;
            },
          });
          let cur;
          // eslint-disable-next-line no-cond-assign
          while ((cur = walker.nextNode())) {
            const parentEl = cur.parentElement;
            const cs = cwin.getComputedStyle(parentEl);
            const fontSizePx = parseFloat(cs.fontSize) || 12;
            const raw = cur.nodeValue;
            const re = /\S+/g;
            let m;
            while ((m = re.exec(raw)) !== null) {
              const start = m.index;
              const end = start + m[0].length;
              const range = clonedDoc.createRange();
              try {
                range.setStart(cur, start);
                range.setEnd(cur, end);
              } catch { continue; }
              const rects = Array.from(range.getClientRects());
              if (!rects.length) continue;
              const r = rects[0];
              const lineHeightPx = r.height || fontSizePx;
              const yBaselinePx = (r.bottom - clonedRect.top) - lineHeightPx / 2 + fontSizePx * 0.55;
              const xPx = r.left - clonedRect.left;
              const renderedWidthPx = r.right - r.left;
              wordPositions.push({ text: m[0], xPx, yBaselinePx, fontSizePx, renderedWidthPx });
            }
          }
        } catch (err) {
          console.warn("[QM-PDF] onclone overlay collection failed:", err?.message || err);
        }
      },
    });
    const uniqBreaks = Array.from(new Set(breakCandidates.map((y) => Math.round(y * 10) / 10))).sort((a, b) => a - b);
    log(`Step 3 done — canvas=${canvas.width}x${canvas.height}, words=${wordPositions.length}, breakCandidates=${uniqBreaks.length} (${((performance.now()-t0)/1000).toFixed(1)}s)`);

    const pdf = new jsPDF({ orientation: "p", unit: "pt", format: "a4" });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imgW = pageWidth;
    const imgH = (canvas.height * imgW) / canvas.width;
    const scale = nodeRect.width > 0 ? imgW / nodeRect.width : imgW / PLANNER_CAPTURE_WIDTH;
    const pageHeightCssPx = pageHeight / scale;
    const totalCssHeightPx = nodeRect.height;
    let textWriteFailures = 0;
    const pageRanges = [];
    let cursor = 0;
    while (cursor < totalCssHeightPx - 0.5) {
      const targetEnd = cursor + pageHeightCssPx;
      let endY;
      if (targetEnd >= totalCssHeightPx) {
        endY = totalCssHeightPx;
      } else {
        let best = -1;
        for (let i = 0; i < uniqBreaks.length; i += 1) {
          const b = uniqBreaks[i];
          if (b <= cursor + 1) continue;
          if (b > targetEnd) break;
          best = b;
        }
        endY = best > 0 ? best : targetEnd;
      }
      pageRanges.push({ yStart: cursor, yEnd: endY });
      cursor = endY;
    }

    const jpegDataUrl = canvas.toDataURL("image/jpeg", 0.92);
    const renderPage = (pdfDoc, pageIdx) => {
      const { yStart, yEnd } = pageRanges[pageIdx];
      pdfDoc.setFont("helvetica", "normal");
      pdfDoc.setTextColor(255, 255, 255);
      for (let i = 0; i < wordPositions.length; i += 1) {
        const w = wordPositions[i];
        if (w.yBaselinePx < yStart - 2 || w.yBaselinePx > yEnd + 2) continue;
        const fontSizePt = Math.max(2, w.fontSizePx * scale);
        pdfDoc.setFontSize(fontSizePt);
        const xPt = w.xPx * scale;
        const yBaselinePt = (w.yBaselinePx - yStart) * scale;
        if (yBaselinePt < 0 || yBaselinePt > pageHeight) continue;
        let charSpace = 0;
        if (w.text.length > 1) {
          const pdfWidthPt = pdfDoc.getTextWidth(w.text);
          const renderedWidthPt = w.renderedWidthPx * scale;
          charSpace = (renderedWidthPt - pdfWidthPt) / (w.text.length - 1);
        }
        try {
          pdfDoc.text(w.text, Math.max(0, xPt), yBaselinePt, { charSpace });
        } catch (err) {
          textWriteFailures += 1;
          if (textWriteFailures <= 3) console.warn("[QuoteMatrix PDF] text overlay failed:", err?.message || err);
        }
      }
      pdfDoc.setTextColor(0, 0, 0);
      const yShiftPt = -yStart * scale;
      pdfDoc.addImage(jpegDataUrl, "JPEG", 0, yShiftPt, imgW, imgH, undefined, "FAST");
      const sliceHeightPt = (yEnd - yStart) * scale;
      if (sliceHeightPt < pageHeight - 0.5) {
        pdfDoc.setFillColor(255, 255, 255);
        pdfDoc.rect(0, sliceHeightPt, pageWidth, pageHeight - sliceHeightPt, "F");
      }
    };

    log(`Step 4/8 — rendering ${pageRanges.length} planner page(s) into jsPDF`);
    for (let i = 0; i < pageRanges.length; i += 1) {
      if (i > 0) pdf.addPage();
      try { renderPage(pdf, i); }
      catch (err) { console.warn("[QuoteMatrix PDF] page render aborted:", err?.message || err); }
    }
    log(`Step 4 done — ${pageRanges.length} planner page(s), ${textWriteFailures} text-overlay failures`);
    node.style.display = prevDisplay;
    node.style.width = prevWidth;
    const plannerPageCount = pageRanges.length;
    const plannerBytes = pdf.output("arraybuffer");
    log(`Step 5/8 — planner PDF generated (${plannerBytes.byteLength} bytes)`);

    const merged = await PDFDocument.create();
    const plannerDoc = await PDFDocument.load(plannerBytes);
    const plannerPages = await merged.copyPages(plannerDoc, plannerDoc.getPageIndices());
    plannerPages.forEach((p) => merged.addPage(p));

    let mergedAttachmentCount = 0;
    let skippedAttachmentCount = 0;
    if (includeAttachments && attachments.length > 0) {
      log(`Step 6/8 — merging ${attachments.length} attachment(s) (parallel fetch)`);

      // Speed-up: kick off all attachment fetches in parallel before walking
      // through them sequentially. The merge step (copyPages / addPage) must
      // stay sequential because pdf-lib's page order is determined by add
      // order, but the network wait dominated total time before (5 PDFs ×
      // 1.5s each ≈ 7.5s now becomes ~1.5s as they fetch concurrently).
      const fetchedBytes = await Promise.all(attachments.map(async (att) => {
        if (!att?.url) return null;
        if (att.kind === "msg" || att.kind === "eml") return null; // rendered from parsed_email, no fetch
        try {
          // `?quote={id}` gives the backend a quote-context bypass so
          // non-admin estimators without `can_download_attachments` can
          // still include attachments in their own Quote Matrix exports.
          // Files need the Bearer token, so go through the authed axios client.
          const path = att.url.replace(/^.*?\/api(?=\/)/, "");
          const res = await api.get(path, { params: { quote: id }, responseType: "arraybuffer" });
          return { bytes: res.data };
        } catch (err) {
          return { error: err?.response?.status ? `HTTP ${err.response.status}` : (err?.message || String(err)) };
        }
      }));

      for (let i = 0; i < attachments.length; i += 1) {
        const att = attachments[i];
        const fetched = fetchedBytes[i];
        try {
          if (att.kind === "pdf" || att.kind === "supplier_pdf" || (!att.kind && /\.pdf$/i.test(att.original_name))) {
            if (!fetched || fetched.error || !fetched.bytes) { skippedAttachmentCount += 1; continue; }
            const attDoc = await PDFDocument.load(fetched.bytes, { ignoreEncryption: true });
            const pages = await merged.copyPages(attDoc, attDoc.getPageIndices());
            pages.forEach((p) => merged.addPage(p));
            mergedAttachmentCount += 1;
          } else if (att.kind === "image") {
            if (!fetched || fetched.error || !fetched.bytes) { skippedAttachmentCount += 1; continue; }
            const isPng = /\.png$/i.test(att.original_name);
            const img = isPng ? await merged.embedPng(fetched.bytes) : await merged.embedJpg(fetched.bytes);
            const page = merged.addPage([595.28, 841.89]);
            const caption = `${att.original_name} · ${att.uploaded_by || ""} · ${fmtAuditDate(att.uploaded_at)}`.trim();
            const helv = await merged.embedFont(StandardFonts.Helvetica);
            page.drawText(caption, { x: 36, y: 805, size: 9, font: helv, color: rgb(0.35, 0.4, 0.5) });
            const maxW = 595.28 - 72;
            const maxH = 805 - 36 - 30;
            const sc = Math.min(maxW / img.width, maxH / img.height, 1);
            const w = img.width * sc;
            const h = img.height * sc;
            page.drawImage(img, { x: (595.28 - w) / 2, y: 36 + (maxH - h) / 2, width: w, height: h });
            mergedAttachmentCount += 1;
          } else if (att.kind === "msg" || att.kind === "eml") {
            if (!att.parsed_email) continue;
            const e = att.parsed_email;
            const wrapper = document.createElement("div");
            wrapper.style.cssText = "position:fixed;left:-99999px;top:0;width:794px;background:#fff;padding:48px 56px;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;color:#0f172a;";
            wrapper.innerHTML = `
              <div style="border-bottom:2px solid #2756C4;padding-bottom:14px;margin-bottom:18px;">
                <div style="font-size:10px;letter-spacing:0.18em;color:#64748b;text-transform:uppercase;font-weight:700;">Attached email · ${att.original_name}</div>
                <div style="font-size:20px;font-weight:800;margin-top:6px;color:#0f172a;line-height:1.3;">${(e.subject || "(no subject)").replace(/</g, "&lt;")}</div>
              </div>
              <table style="width:100%;font-size:11px;margin-bottom:18px;color:#334155;">
                <tr><td style="padding:2px 8px 2px 0;color:#64748b;text-transform:uppercase;font-size:9px;letter-spacing:0.05em;font-weight:600;width:48px;">From</td><td>${(e.from_addr || "—").replace(/</g, "&lt;")}</td></tr>
                <tr><td style="padding:2px 8px 2px 0;color:#64748b;text-transform:uppercase;font-size:9px;letter-spacing:0.05em;font-weight:600;">To</td><td>${(e.to || "—").replace(/</g, "&lt;")}</td></tr>
                ${e.cc ? `<tr><td style="padding:2px 8px 2px 0;color:#64748b;text-transform:uppercase;font-size:9px;letter-spacing:0.05em;font-weight:600;">Cc</td><td>${e.cc.replace(/</g, "&lt;")}</td></tr>` : ""}
                <tr><td style="padding:2px 8px 2px 0;color:#64748b;text-transform:uppercase;font-size:9px;letter-spacing:0.05em;font-weight:600;">Sent</td><td>${e.sent_at ? fmtAuditDate(e.sent_at) || e.sent_at : "—"}</td></tr>
              </table>
              <div style="font-size:12px;line-height:1.55;white-space:pre-wrap;color:#1e293b;">${(e.body_text || "").replace(/</g, "&lt;")}</div>
            `;
            document.body.appendChild(wrapper);
            try {
              const cnv = await html2canvas(wrapper, { scale: 2, backgroundColor: "#ffffff", useCORS: true });
              const png = cnv.toDataURL("image/png");
              const tmpPdf = new jsPDF({ orientation: "p", unit: "pt", format: "a4" });
              const pw = tmpPdf.internal.pageSize.getWidth();
              const ph = tmpPdf.internal.pageSize.getHeight();
              const ratio = cnv.height / cnv.width;
              let drawH = pw * ratio;
              let drawW = pw;
              if (drawH > ph) { drawH = ph; drawW = ph / ratio; }
              tmpPdf.addImage(png, "PNG", (pw - drawW) / 2, 0, drawW, drawH);
              const tmpBytes = tmpPdf.output("arraybuffer");
              const tmpDoc = await PDFDocument.load(tmpBytes);
              const pages = await merged.copyPages(tmpDoc, tmpDoc.getPageIndices());
              pages.forEach((p) => merged.addPage(p));
              mergedAttachmentCount += 1;
            } finally {
              wrapper.remove();
            }
          }
        } catch (err) {
          skippedAttachmentCount += 1;
          console.warn("Skipping attachment", att.original_name, err);
        }
      }
      log(`Step 6 done — merged ${mergedAttachmentCount}, skipped ${skippedAttachmentCount}`);
    }

    log("Step 7/8 — serialising merged PDF");
    const out = await merged.save();
    const safe = (s) => (s || "").replace(/[\\/:*?"<>|]+/g, " ").trim();
    const fileName = `Quote Matrix - ${safe(q.customer_name) || "customer"} - ${safe(q.quote_number) || "quote"}${includeAttachments && attachments.length ? " (with attachments)" : ""}.pdf`;

    const blob = new Blob([out], { type: "application/pdf" });
    // Production / standalone domain → skip the temporary-export upload
    // round-trip and go straight to a blob: URL. On the Emergent preview the
    // app runs inside a sandboxed iframe that silently blocks blob downloads,
    // so we keep the temp-export upload path for that case (detected via
    // `window !== window.top`). Saves a full PDF-byte round-trip in prod —
    // typically 2-5s on a quote with merged attachments.
    const isInIframe = (() => { try { return window !== window.top; } catch { return true; } })();
    let downloadUrl = null;
    if (isInIframe) {
      log(`Step 8/8 — iframe sandbox detected, uploading ${out.byteLength} bytes for download (name="${fileName}")`);
      const form = new FormData();
      form.append("file", blob, fileName);
      try {
        const { data } = await api.post(`/quotes/${id}/temporary-export`, form);
        if (data && data.url) {
          downloadUrl = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
        }
      } catch (err) {
        console.warn("[QM-PDF] temporary-export upload failed, falling back to blob URL:", err?.message || err);
      }
    } else {
      log(`Step 8/8 — direct blob download (${out.byteLength} bytes, name="${fileName}")`);
    }
    const fallbackBlobUrl = downloadUrl ? null : URL.createObjectURL(blob);
    const finalUrl = downloadUrl || fallbackBlobUrl;

    const triggerTopDownload = () => {
      try {
        const a = document.createElement("a");
        a.href = finalUrl;
        a.download = fileName;
        a.target = "_top";
        a.rel = "noopener";
        document.body.appendChild(a);
        a.click();
        a.remove();
        return true;
      } catch (err) {
        console.warn("[QM-PDF] top-target anchor click failed:", err?.message || err);
        return false;
      }
    };
    const downloadTriggered = triggerTopDownload();
    // If any attachments failed the fetch (usually 403 due to permission
    // config), tell the user explicitly so they don't assume the PDF is
    // complete when it silently isn't.
    if (skippedAttachmentCount > 0) {
      toast.warning(
        `${skippedAttachmentCount} attachment${skippedAttachmentCount === 1 ? "" : "s"} couldn't be included in the PDF. ` +
        `Ask an admin to enable "Download attachments" on your Users → Permissions row.`,
        { duration: 12000 }
      );
    }
    if (isInIframe) {
      toast.success(`PDF ready — ${plannerPageCount} planner page(s)${mergedAttachmentCount ? ` + ${mergedAttachmentCount} attachment(s)` : ""}`, {
        action: {
          label: "Download",
          onClick: () => {
            if (!triggerTopDownload()) {
              window.open(finalUrl, "_blank", "noopener,noreferrer");
            }
          },
        },
        duration: 8000,
      });
    }
    if (fallbackBlobUrl) {
      setTimeout(() => URL.revokeObjectURL(fallbackBlobUrl), 60000);
    }
    log(`Done in ${((performance.now() - t0) / 1000).toFixed(1)}s, downloadTriggered=${downloadTriggered}, iframe=${isInIframe}`);
  } catch (e) {
    console.error("[QM-PDF] FAILED:", e);
    toast.error(`PDF export failed: ${e.message}`);
    const n = document.getElementById("planner-sheet");
    if (n && n.style.display === "block") n.style.display = "none";
  } finally {
    setBusy(false);
  }
}
